import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Loader2, ArrowRight, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { getMe, loginWithGoogle } from "@/lib/auth";

export default function Pembayaran() {
  const navigate = useNavigate();
  const [state, setState] = useState("loading"); // loading | guest | paid | unpaid | empty
  const [info, setInfo] = useState(null);
  const [checking, setChecking] = useState(false);

  const load = useCallback(async () => {
    try {
      await getMe();
    } catch {
      setState("guest");
      return;
    }
    try {
      const { data } = await api.get("/user/latest-result");
      setInfo(data);
      if (data.paid && data.public_id) setState("paid");
      else if (data.public_id) setState("unpaid");
      else setState("empty");
    } catch (e) {
      setState("empty");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (state !== "unpaid") return undefined;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get("/user/latest-result");
        setInfo(data);
        if (data.paid && data.public_id) setState("paid");
      } catch (e) { /* retry on next tick */ }
    }, 4000);
    return () => clearInterval(iv);
  }, [state]);

  const checkNow = async () => {
    setChecking(true);
    try {
      const { data } = await api.get("/user/latest-result");
      setInfo(data);
      if (data.paid && data.public_id) setState("paid");
    } catch (e) { /* ignore */ }
    setChecking(false);
  };

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center px-5">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md glass rounded-3xl p-8 sm:p-10 text-center" data-testid="payment-page">

        {state === "loading" && (
          <div className="py-10 flex flex-col items-center gap-4" data-testid="payment-loading">
            <Loader2 size={32} className="text-amber-300 animate-spin" />
            <p className="text-sm text-gray-400">Mengecek status pembayaran lo…</p>
          </div>
        )}

        {state === "guest" && (
          <div data-testid="payment-guest">
            <div className="w-12 h-12 mx-auto rounded-full bg-amber-400/15 border border-amber-400/40 flex items-center justify-center mb-5">
              <Lock size={20} className="text-amber-300" />
            </div>
            <h1 className="font-display text-2xl sm:text-3xl">Login dulu ya</h1>
            <p className="mt-3 text-sm text-gray-400">
              Biar kami bisa nyocokin pembayaran lo sama akun Google yang dipakai pas test.
            </p>
            <button onClick={loginWithGoogle} data-testid="payment-login-button"
              className="mt-7 gold-btn rounded-full px-8 py-4 inline-flex items-center gap-2">
              Login pakai Google <ArrowRight size={17} />
            </button>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="payment-paid">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-400/15 border border-emerald-400/40 flex items-center justify-center mb-5">
              <CheckCircle size={26} className="text-emerald-300" />
            </div>
            <h1 className="font-display text-2xl sm:text-3xl">Pembayaran <span className="gold-text">berhasil!</span></h1>
            <p className="mt-3 text-sm text-gray-400">
              Semua hasil lengkap lo udah kebuka — semua kategori, selamanya.
            </p>
            <button onClick={() => navigate(`/hasil/${info.public_id}`)} data-testid="payment-open-result-button"
              className="mt-7 gold-btn rounded-full px-8 py-4 inline-flex items-center gap-2">
              Buka Hasil Gue <ArrowRight size={17} />
            </button>
          </div>
        )}

        {state === "unpaid" && (
          <div data-testid="payment-pending">
            <div className="w-14 h-14 mx-auto rounded-full bg-amber-400/15 border border-amber-400/40 flex items-center justify-center mb-5">
              <Loader2 size={24} className="text-amber-300 animate-spin" />
            </div>
            <h1 className="font-display text-2xl sm:text-3xl">Pembayaran lagi <span className="gold-text">diverifikasi…</span></h1>
            <p className="mt-3 text-sm text-gray-400">
              Kalau lo baru aja bayar, konfirmasi biasanya butuh beberapa detik. Halaman ini ngecek otomatis.
            </p>
            <button onClick={checkNow} disabled={checking} data-testid="payment-check-button"
              className="mt-7 gold-btn rounded-full px-8 py-4 inline-flex items-center gap-2 disabled:opacity-60">
              {checking ? "Mengecek…" : "Cek Status Pembayaran"}
            </button>
            {info?.public_id && (
              <button onClick={() => navigate(`/hasil/${info.public_id}`)} data-testid="payment-back-result-button"
                className="mt-4 text-sm text-gray-400 hover:text-amber-200 transition-colors underline underline-offset-2">
                Balik ke halaman hasil
              </button>
            )}
          </div>
        )}

        {state === "empty" && (
          <div data-testid="payment-empty">
            <h1 className="font-display text-2xl sm:text-3xl">Belum ada hasil test</h1>
            <p className="mt-3 text-sm text-gray-400">
              Kerjain test dulu — gratis — terus buka hasil lengkapnya kalau lo penasaran.
            </p>
            <button onClick={() => navigate("/pilih")} data-testid="payment-start-test-button"
              className="mt-7 gold-btn rounded-full px-8 py-4 inline-flex items-center gap-2">
              Mulai Test <ArrowRight size={17} />
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
